import { useEffect } from "react";
import { pubSub } from "../ably";
import { ADMIN_CHANNELS } from "../channel/channels.js";
import { normalizePollList } from "../channel/normalizePayload.js";
import { setAdminPoll, getSessionById } from "./adminStore.js";
import { applyKeyedListMessage } from "../realtime/channelSyncEngine.js";

function pollItemId(item) {
  return String(item?.id);
}

function bankConfig(sessionId) {
  return {
    getId: pollItemId,
    classify(raw) {
      const list = normalizePollList(raw);
      return list && list.length ? { kind: "items", items: list } : { kind: "ignore", items: [] };
    },
    getCurrent: () => getSessionById(sessionId)?.polls || [],
    setResult: (nextList) => setAdminPoll(sessionId, nextList),
  };
}

function applyBankItems(cacheKey, sessionId, items) {
  // Ably hands history back newest-first; replay oldest-first so the per-item
  // freshness check inside applyKeyedListMessage only ever moves forward.
  [...items].reverse().forEach((item) => {
    try {
      applyKeyedListMessage({
        cacheKey,
        config: bankConfig(sessionId),
        message: { id: item.id, timestamp: item.timestamp, raw: item.data },
      });
    } catch (e) {
      console.warn("[useAdminPollSync] history apply failed", e);
    }
  });
}

export default function useAdminPollSync(sessionId) {
  useEffect(() => {
    if (!sessionId) return;
    const cacheKey = ["admin-poll-bank", Number(sessionId)];

    const handler = (data, _isHistory, meta = {}) => {
      try {
        applyKeyedListMessage({
          cacheKey,
          config: bankConfig(sessionId),
          message: { id: meta.id, timestamp: meta.timestamp, raw: data },
        });
      } catch (e) {
        console.warn("[useAdminPollSync] failed", e);
      }
    };
    pubSub.subscribe(ADMIN_CHANNELS.ADMIN_POLL, handler);
    return () => pubSub.unsubscribe(ADMIN_CHANNELS.ADMIN_POLL, handler);
  }, [sessionId]);

  // Seed the bank from channel history (see useAdminQnaSync): the one-shot
  // Ably replay can be missed by a late-mounted hook, and a fresh page load
  // would otherwise show an empty poll bank until something is published.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const cacheKey = ["admin-poll-bank", Number(sessionId)];

    const seed = async () => {
      try {
        const items = await pubSub.getChannelHistoryWithMeta(ADMIN_CHANNELS.ADMIN_POLL, {
          limit: 100,
          maxPages: 1,
        });
        if (cancelled || !items?.length) return;
        applyBankItems(cacheKey, sessionId, items);
      } catch (e) {
        console.warn("[useAdminPollSync] history seed failed", e);
      }
    };

    if (pubSub.isConnected) seed();
    const onConn = (connected) => {
      if (connected) seed();
    };
    pubSub.onConnectionChange(onConn);
    return () => {
      cancelled = true;
      pubSub.offConnectionChange(onConn);
    };
  }, [sessionId]);
}
